import { supabase } from '../../lib/supabase';
import type { User, UserRole } from '../../contexts/auth.types';
import {
  type RpcProfileWithRoles,
  type RpcUserRole,
  type LegacyProfileRow,
  type LegacyRoleAssignmentWithPermissions,
} from './types';
import { PROFILE_LOAD_TIMEOUT_MS, RPC_TIMEOUT_MS, runWithAbortTimeout, maybeAttachAbortSignal } from './timeoutUtils';
import {
  resolveCompanyContextFromRpc,
  fetchTradeCompanyStatus,
  fetchCustomerStatus,
  fetchDirectJobSettings,
} from './authUtils';

async function fetchAnalyticsExclusion(): Promise<boolean> {
  try {
    if (typeof (supabase as unknown as { rpc?: unknown })?.rpc !== 'function') {
      return false;
    }
    const { data, error } = await supabase.rpc('is_analytics_excluded');
    if (error) {
      console.debug('[auth] is_analytics_excluded error:', error);
      return false;
    }
    return data === true;
  } catch (err) {
    console.debug('[auth] is_analytics_excluded exception:', err);
    return false;
  }
}

export async function loadUserProfile(userId: string, userEmail?: string | null): Promise<User | null> {
  const legacyLoad = async (): Promise<User | null> => {
    const { data: profileData, error: profileError } = await runWithAbortTimeout(
      'profiles.select(single)',
      (signal) => {
        const query = supabase.from('profiles').select('*').eq('id', userId);
        return maybeAttachAbortSignal(query, signal).maybeSingle();
      },
      PROFILE_LOAD_TIMEOUT_MS
    );

    if (profileError || !profileData) {
      return null;
    }

    const profile = profileData as unknown as LegacyProfileRow;

    const { data: roleAssignmentsData, error: rolesError } = await runWithAbortTimeout(
      'user_role_assignments.select',
      (signal) => {
        const query = supabase
          .from('user_role_assignments')
          .select(`
            id,
            assigned_at,
            assigned_by,
            expires_at,
            role_id,
            user_id,
            roles:roles (
              role_name,
              description,
              role_permissions:role_permissions (
                permissions:permissions (
                  permission_name,
                  resource_type
                )
              )
            )
          `)
          .eq('user_id', userId)
          .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);

        return maybeAttachAbortSignal(query, signal);
      },
      PROFILE_LOAD_TIMEOUT_MS
    );

    if (rolesError) {
      console.error('❌ Legacy roles load error:', rolesError);
    }

    const roles: UserRole[] = [];
    const permissionSet = new Set<string>();

    const companyType: UserRole['company_type'] = profile.customer_id
      ? 'customer'
      : profile.trade_company_id
        ? 'trade_company'
        : 'platform';
    const companyId: string | null = profile.customer_id ?? profile.trade_company_id ?? null;

    if (Array.isArray(roleAssignmentsData)) {
      (roleAssignmentsData as unknown as LegacyRoleAssignmentWithPermissions[]).forEach((assignment) => {
        const role = assignment.roles;
        if (!role) return;

        roles.push({
          role_name: role.role_name,
          role_description: role.description ?? '',
          company_id: companyId,
          company_type: companyType,
        });

        role.role_permissions?.forEach((relationship) => {
          const permissionName = relationship?.permissions?.permission_name;
          if (permissionName) {
            permissionSet.add(permissionName);
          }
        });
      });
    }

    const permissions = Array.from(permissionSet);
    const context = resolveCompanyContextFromRpc(profile as unknown as RpcProfileWithRoles);

    const email = profile.email || userEmail || '';
    const firstName = profile.first_name ?? '';
    const lastName = profile.last_name ?? '';

    const [tradeCompanyStatus, customerStatus, directJobsEnabled, isAnalyticsExcluded] = await Promise.all([
      fetchTradeCompanyStatus(profile.trade_company_id),
      fetchCustomerStatus(profile.customer_id),
      fetchDirectJobSettings(profile.trade_company_id),
      fetchAnalyticsExclusion(),
    ]);

    return {
      id: profile.id,
      email,
      first_name: firstName,
      last_name: lastName,
      name: `${firstName} ${lastName}`.trim() || email.split('@')[0] || 'User',
      roles,
      permissions,
      account_type: context.accountType,
      account_mode: context.accountMode,
      primary_account_type: context.primaryAccountType,
      active_role: context.activeRole,
      company_id: context.companyId,
      customer_id: profile.customer_id,
      trade_company_id: profile.trade_company_id,
      trade_company_status: tradeCompanyStatus,
      is_platform_admin: !!profile.is_platform_admin,
      customer_status: customerStatus,
      direct_jobs_enabled: directJobsEnabled,
      impersonated_by_admin_id: null,
      is_delegated_acceptor: profile.is_delegated_acceptor ?? false,
      delegation_expires_at: profile.delegation_expires_at ?? null,
      delegation_company_id: profile.delegation_company_id ?? null,
      // The legacy path has no company_role_settings join; canAcceptJob treats
      // an absent override object the same as "no overrides configured".
      company_role_settings: null,
      is_analytics_excluded: isAnalyticsExcluded,
    };
  };

  if (typeof (supabase as unknown as { rpc?: unknown }).rpc !== 'function') {
    return legacyLoad();
  }

  let rpcRow: unknown = null;
  let rpcError: unknown = null;

  try {
    const result = await runWithAbortTimeout(
      'sync_user_session',
      (signal) =>
        supabase
          .rpc('sync_user_session', { user_id: userId })
          .abortSignal(signal)
          .single(),
      RPC_TIMEOUT_MS
    );
    rpcRow = result.data;
    rpcError = result.error;
  } catch (err) {
    console.warn('⚠️ RPC call timed out or failed, falling back to legacy load:', err);
    return legacyLoad();
  }

  if (rpcError || !rpcRow) {
    console.warn('⚠️ RPC returned error or no data, falling back to legacy load:', rpcError);
    return legacyLoad();
  }

  const profile = rpcRow as unknown as RpcProfileWithRoles;
  const roles: UserRole[] = Array.isArray(profile.roles)
    ? (profile.roles as RpcUserRole[]).map((r) => ({
      role_name: r.role_name,
      role_description: r.role_description ?? '',
      company_id: r.company_id,
      company_type: r.company_type,
    }))
    : [];

  const permissions = Array.isArray(profile.permissions) ? profile.permissions : [];

  const { accountType, companyId, accountMode, primaryAccountType, activeRole } =
    resolveCompanyContextFromRpc(profile);

  const email = profile.email || userEmail || '';
  const firstName = profile.first_name ?? '';
  const lastName = profile.last_name ?? '';

  const [tradeCompanyStatus, customerStatus, directJobsEnabled, isAnalyticsExcluded] = await Promise.all([
    fetchTradeCompanyStatus(profile.trade_company_id),
    fetchCustomerStatus(profile.customer_id),
    fetchDirectJobSettings(profile.trade_company_id),
    fetchAnalyticsExclusion(),
  ]);

  const userData: User = {
    id: profile.id,
    email,
    first_name: firstName,
    last_name: lastName,
    name: `${firstName} ${lastName}`.trim() || email.split('@')[0] || 'User',
    roles,
    permissions,
    account_type: accountType,
    account_mode: accountMode,
    primary_account_type: primaryAccountType,
    active_role: activeRole,
    company_id: companyId,
    customer_id: profile.customer_id,
    trade_company_id: profile.trade_company_id,
    trade_company_status: tradeCompanyStatus,
    is_platform_admin: !!profile.is_platform_admin,
    customer_status: customerStatus,
    direct_jobs_enabled: directJobsEnabled,
    impersonated_by_admin_id: profile.impersonated_by_admin_id ?? null,
    is_delegated_acceptor: profile.is_delegated_acceptor ?? false,
    delegation_expires_at: profile.delegation_expires_at ?? null,
    delegation_company_id: profile.delegation_company_id ?? null,
    company_role_settings: profile.company_role_settings ?? null,
    is_analytics_excluded: isAnalyticsExcluded,
  };

  return userData;
}
